/**
 * Entity Sync - v13 Section 2.9
 *
 * Syncs key people from Ikigai analysis to entities namespace,
 * allowing mission agents to reference known people.
 *
 * @see docs/sprints/ownyou-sprint6-spec.md
 * @see docs/architecture/OwnYou_architecture_v13.md Section 2.9
 */
import { NS } from '@ownyou/shared-types';
/**
 * Sync key people to entities namespace - v13 Section 2.9
 *
 * Allows mission agents to reference known people.
 */
export async function syncPeopleToEntities(userId, people, store) {
    for (const person of people) {
        const entityKey = `person:${normalizeEntityKey(person.name)}`;
        await store.put(NS.entities(userId), entityKey, {
            entityType: 'person',
            name: person.name,
            relationship: person.relationshipType,
            interactionFrequency: person.interactionFrequency,
            sharedInterests: person.sharedInterests,
            sharedActivities: person.sharedActivities,
            relationshipStrength: person.relationshipStrength,
            lastSeen: new Date().toISOString(),
            sourceContext: 'ikigai_inference',
        });
    }
}
/**
 * Get known people for an agent
 */
export async function getKnownPeople(userId, store) {
    try {
        const result = await store.list(NS.entities(userId));
        return result.items
            .filter(val => val.entityType === 'person')
            .map((val) => ({
            name: String(val.name || ''),
            relationshipType: val.relationship || 'other',
            interactionFrequency: val.interactionFrequency || 'occasional',
            sharedInterests: val.sharedInterests ?? [],
            sharedActivities: val.sharedActivities ?? [],
            relationshipStrength: Number(val.relationshipStrength) || 0.5,
            evidence: String(val.name || ''),
        }));
    }
    catch {
        return [];
    }
}
/**
 * Get a specific person by name
 */
export async function getPersonByName(userId, name, store) {
    try {
        const entityKey = `person:${normalizeEntityKey(name)}`;
        const result = await store.get(NS.entities(userId), entityKey);
        if (!result)
            return undefined;
        const val = result;
        return {
            name: String(val.name || ''),
            relationshipType: val.relationship || 'other',
            interactionFrequency: val.interactionFrequency || 'occasional',
            sharedInterests: val.sharedInterests ?? [],
            sharedActivities: val.sharedActivities ?? [],
            relationshipStrength: Number(val.relationshipStrength) || 0.5,
            evidence: entityKey,
        };
    }
    catch {
        return undefined;
    }
}
/**
 * Find people matching a relationship type
 */
export async function getPeopleByRelationship(userId, relationshipType, store) {
    const allPeople = await getKnownPeople(userId, store);
    return allPeople.filter((p) => p.relationshipType === relationshipType);
}
/**
 * Find people with high relationship strength
 */
export async function getClosestPeople(userId, store, limit = 5) {
    const allPeople = await getKnownPeople(userId, store);
    return allPeople
        .sort((a, b) => b.relationshipStrength - a.relationshipStrength)
        .slice(0, limit);
}
/**
 * Normalize entity key for consistent lookups
 */
function normalizeEntityKey(name) {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special chars
        .replace(/\s+/g, '_') // Spaces to underscores
        .substring(0, 50); // Limit length
}
//# sourceMappingURL=entity-sync.js.map